import {
    Alignment, AlignmentUtil, Color, DOMUtil,
    getThemeForKey, JSUtil, Logger, LogManager,
    StringUtil
} from "@mcleod/core";
import { Component, ComponentCreator, ComponentFactory, LabelProps, Panel, PanelProps, ScreenStack } from "..";
import { MouseEvent } from "../events/MouseEvent";
import { AnchorMutation } from "./Anchor";
import { AnchoredComponentPositioner } from "./positioner/AnchoredComponentPositioner";

const _scrollListener: EventListener = (event: Event) => hideTooltipsOnScroll(event);

export interface TooltipOptions {
    shaking: boolean;
    warning: boolean
    timeout: number;
    position: Alignment;
    anchorToMouse: boolean;
    originatingEvent: MouseEvent;
    showPointer: boolean;
    pointerColor: Color | ((position: Alignment) => Color)
}

/**
 *
 * @param {*} anchor
 * @param {*} tipOrComponentOrLabelProps
 * @param {*} options
 * @returns
 */
export function showTooltip(anchor: Component, tipOrComponentOrLabelProps: ComponentCreator, options?: Partial<TooltipOptions>, props?: Partial<PanelProps>): Panel {
    initializeScrollListener();
    const comp = ComponentFactory.createCommon(tipOrComponentOrLabelProps, null, anchor);
    if (comp == null)
        return;
    props = JSUtil.assignIfUndefined(getThemeForKey(props?.themeKey ?? "tooltip"), props);
    //remove theme key from props since we just applied the individual theme key values to props...
    //there is no need to apply the theme key property to the component, it can only apply properties we may not want
    delete props.themeKey;
    if (_newTooltipAnchoredToAnotherTooltip(anchor) !== true)
        hideTooltip();
    return new Tooltip(anchor, comp, options, props);
}

export function showBulletedTooltip(anchor: Component, values: Partial<LabelProps>[],
    options?: Partial<TooltipOptions>, props?: Partial<PanelProps>): Panel {
    const labels = ComponentFactory.createBulletedList(values);
    const panel = Panel.createNoMarginNoPaddingPanel();
    panel.add(...labels);
    return showTooltip(anchor, panel, options, props);
}

function initializeScrollListener() {
    Tooltip.log.debug("Adding scroll listener for the purpose of hiding all tooltips");
    const options = { capture: true, once: true };
    window.addEventListener("scroll", _scrollListener, options);
}

/**
 * Hides all visible tooltips, unless the element being scrolled is in one of the visible tooltips.
 * @param event The scroll event
 */
function hideTooltipsOnScroll(event: Event) {
    const scrollTarget = event.target;
    const tooltipStackCopy = [...ScreenStack.getAllTooltips()];
    for (const tooltip of tooltipStackCopy) {
        if (DOMUtil.isOrContains(tooltip._element, scrollTarget as Element)) {
            Tooltip.log.debug("Not hiding tooltip(s) due to scroll that occurred inside a tooltip %o", event);
            return;
        }
    }
    Tooltip.log.debug("Hiding tooltip(s) due to scroll that occurred outside of tooltip %o", event);
    hideTooltip();
}

function _newTooltipAnchoredToAnotherTooltip(anchor: Component): boolean {
    if (anchor == null)
        return false;
    for (const existingTooltip of ScreenStack.getAllTooltips()) {
        if (existingTooltip.isOrContains(anchor))
            return true;
    }
    return false;
}

export function hideTooltip(tooltip?: Panel) {
    const oldestTooltipToHide = tooltip == null ? ScreenStack.getOldestTooltip() : tooltip;
    if (oldestTooltipToHide == null)
        return;
    const tooltipStackCopy = [...ScreenStack.getAllTooltips()];
    const tooltipToHideIndex = tooltipStackCopy.indexOf(oldestTooltipToHide);
    if (tooltip != null && tooltipToHideIndex < 0)
        return;
    for (let x = tooltipStackCopy.length - 1; x >= tooltipToHideIndex; x--) {
        const t = tooltipStackCopy[x];
        ScreenStack.popTooltip(t);
        if (t != null && document.body.contains(t._element))
            document.body.removeChild(t._element);
    }
}

export class Tooltip extends Panel implements TooltipOptions {
    private static _log: Logger;
    readonly pointer: Panel;
    readonly anchor: Component;
    readonly shaking: boolean;
    readonly warning: boolean;
    readonly timeout: number;
    readonly position: Alignment;
    readonly anchorToMouse: boolean;
    readonly originatingEvent: MouseEvent;
    readonly showPointer: boolean;
    readonly pointerColor: Color | ((position: Alignment) => Color)
    readonly tooltipContentPanel: Panel;

    constructor(anchor: Component, comp: Component, options?: Partial<TooltipOptions>, contentProps?: Partial<PanelProps>) {
        super({ id: "tooltip_" + new Date().getTime(), scrollY: false, padding: 0, width: null, height: null });
        Object.assign(this, options ?? {});
        this.anchor = anchor;
        this.zIndex = ScreenStack.getNewHighestZIndex() ?? 5000;
        this.addMouseListeners();

        this.tooltipContentPanel = new Panel({
            id: "tooltipContentPanel",
            borderRadius: 4,
            paddingLeft: 16,
            paddingRight: 16,
            rowBreak: false,
            borderShadow: true,
            components: [comp],
            ...contentProps,
        });

        this.add(this.tooltipContentPanel);

        this.setStyleProps({
            overflow: "visible",
            position: "absolute",
            backgroundColor: "transparent",
            visibility: "hidden" // hide the tooltip until we set it's position
        });

        document.body.appendChild(this._element);

        if (anchor != null) {
            AnchoredComponentPositioner.autoUpdateTooltipPosition({
                anchorProps: {
                    anchor: anchor,
                    position: this.position,
                    align: Alignment.CENTER,
                    onMutate: (elem: HTMLElement, mutation: AnchorMutation) => {
                        this.tooltipContentPanel.borderShadow = mutation.newAnchor.position !== Alignment.TOP;
                    }
                },
                anchoredComponent: this,
                showPointer: this.showPointer !== false,
                pointerColor: (position: Alignment) => this.determinePointerColor(position)
            }).then(() => this.style.visibility = "visible");
        } else {
            this.style.visibility = "visible";
        }

        ScreenStack.pushTooltip(this);

        if (this.timeout != null)
            setTimeout(hideTooltip, options.timeout);
        if (this.shaking === true)
            this.shake(8);
    }

    determinePointerColor(position: Alignment): Color {
        let result = JSUtil.evaluateProp(this.pointerColor, position)
        if (StringUtil.isEmptyString(result))
            result = this.getBorderColorByPosition(position);
        if (StringUtil.isEmptyString(result))
            result = this.tooltipContentPanel.backgroundColor;
        return result;
    }

    getBorderColorByPosition(position: Alignment): string {
        const oppositePosition = AlignmentUtil.getOppositeAlignment(position);
        const styleName = `border${StringUtil.capitalize(oppositePosition)}Color`;
        const borderColor = this.tooltipContentPanel.style[styleName];
        if (!StringUtil.isEmptyString(borderColor))
            return borderColor;
    }

    private addMouseListeners() {
        if (this.anchor != null)
            this.addMouseEnterListener(event => this.anchor.clearTooltipHideTimeout());
        this.addMouseLeaveListener(event => {
            if (this.anchor == null || !this.anchor._element.contains(event.domEvent.toElement)) {
                const mouseOverTooltipIndex = this.getMouseOverTooltipIndex(event.domEvent.relatedTarget);
                //if the mouse isn't over any tooltip in the stack, hide them all
                if (mouseOverTooltipIndex < 0)
                    hideTooltip();
                else {
                    //if the mouse is over a tooltip, hide everything newer than that tooltip
                    const tooltipStackCopy = [...ScreenStack.getAllTooltips()];
                    for (let x = tooltipStackCopy.length - 1; x > mouseOverTooltipIndex; x--) {
                        hideTooltip(tooltipStackCopy[x]);
                    }
                }
            }
        });
    }

    private getMouseOverTooltipIndex(mouseOverElement: HTMLElement): number {
        const tooltipStackCopy = [...ScreenStack.getAllTooltips()];
        for (let x = 0; x <= tooltipStackCopy.length - 1; x++) {
            if (DOMUtil.isOrContains(tooltipStackCopy[x]._element, mouseOverElement))
                return x;
        }
        return -1;
    }

    private shake(times: number) {
        if (times > 0) {
            setTimeout(() => {
                const degrees = (times % 2) === 0 ? -1 : 1;
                this._element.style.transform = "rotate(" + degrees + "deg)";
                this.shake(times - 1);
            }, 50);
        }
        else {
            this._element.style.transform = "rotate(0deg)";
        }
    }

    public static get log() {
        if (Tooltip._log == null)
            Tooltip._log = LogManager.getLogger("components/page/tooltip/Tooltip");
        return Tooltip._log;
    }
}
